import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import imageCompression from "browser-image-compression";
import { db } from "@/firebase";
import { DANGER_PHOTO_MAX_SIZE, sortDangers } from "@/utils/dangers";

// Danger reports are public (read by anyone, also when prerendering). firestore.rules decides who may write what:
// signed-in users report, members add check-ups and archive, authors edit their report, admins do everything.
const dangersRef = db.collection("sectionsDangers");
const dangerFlagsRef = db.collection("dangerFlags");

function serverTimestamp() {
  return firebase.firestore.FieldValue.serverTimestamp();
}

function userRef(user) {
  return { uid: user.uid, displayName: user.displayName || "" };
}

// Fields the reporter fills in; the author may edit only these later (firestore.rules).
function pickContent(danger) {
  return {
    rapid_name: (danger.rapid_name || "").trim(),
    type: danger.type,
    type_other: danger.type === "autre" ? (danger.type_other || "").trim() : "",
    impacts: Array.isArray(danger.impacts) ? [...danger.impacts] : [],
    can_stop: danger.can_stop,
    can_stop_other: danger.can_stop === "autre" ? (danger.can_stop_other || "").trim() : "",
    details: (danger.details || "").trim(),
    images: Array.isArray(danger.images) ? danger.images.map(({ url, path }) => ({ url, path })) : [],
    start_date: danger.start_date,
  };
}

function toDanger(doc) {
  return { ...doc.data(), id: doc.id };
}

export const DangersService = {
  actions: {
    async getSectionDangers(sectionId) {
      if (!sectionId) return [];
      // Sorted here rather than with orderBy, so the query needs no composite index.
      const snapshot = await dangersRef.where("section_id", "==", sectionId).get();
      return sortDangers(snapshot.docs.map(toDanger));
    },
    async getActiveDangers() {
      const snapshot = await dangersRef.where("archived", "==", false).get();
      return sortDangers(snapshot.docs.map(toDanger));
    },
    // A new document id, so the photos can be uploaded to dangers/{sectionId}/{id}/ before the danger is saved.
    newDangerId() {
      return dangersRef.doc().id;
    },
    async createDanger(section, danger, user, id = dangersRef.doc().id) {
      await dangersRef.doc(id).set({
        ...pickContent(danger),
        section_id: section.id,
        section_alias: section.alias,
        section_name: section.name,
        river: section.river,
        created_by: userRef(user),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        checkups: [],
        last_checkup: null,
        archived: false,
        archived_by: null,
        archived_at: null,
      });
      return id;
    },
    async updateDanger(id, danger) {
      await dangersRef.doc(id).update({ ...pickContent(danger), updated_at: serverTimestamp() });
    },
    // checkup: { date: "YYYY-MM-DD", comment }. The full history is kept; last_checkup is what the card shows.
    async addCheckup(id, checkup, user) {
      const entry = {
        date: checkup.date,
        comment: (checkup.comment || "").trim(),
        by: userRef(user),
        // Makes each entry unique, so arrayUnion always appends.
        at: new Date().toISOString(),
      };
      await dangersRef.doc(id).update({
        checkups: firebase.firestore.FieldValue.arrayUnion(entry),
        last_checkup: entry,
        updated_at: serverTimestamp(),
      });
      return entry;
    },
    async setArchived(id, archived, user) {
      await dangersRef.doc(id).update({
        archived: Boolean(archived),
        archived_by: archived ? userRef(user) : null,
        archived_at: archived ? serverTimestamp() : null,
        updated_at: serverTimestamp(),
      });
    },
    // Resizes the photo (1600 px max) and uploads it under dangers/{sectionId}/{dangerId}/{uid}/ (see storage.rules).
    async uploadDangerPhoto(sectionId, dangerId, user, file) {
      // No web worker: the library would load its worker script from a CDN.
      const resized = await imageCompression(file, {
        maxWidthOrHeight: DANGER_PHOTO_MAX_SIZE,
        maxSizeMB: 1.5,
        fileType: "image/jpeg",
        initialQuality: 0.85,
        useWebWorker: false,
      });
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
      const path = `dangers/${sectionId}/${dangerId}/${user.uid}/${fileName}`;
      const snapshot = await firebase.storage().ref(path).put(resized, { contentType: "image/jpeg" });
      return { url: await snapshot.ref.getDownloadURL(), path };
    },
    async deleteDangerPhoto(path) {
      await firebase.storage().ref(path).delete();
    },
    async getDanger(id) {
      const doc = await dangersRef.doc(id).get();
      return doc.exists ? toDanger(doc) : null;
    },
    // Admins only (moderation). The photos go too.
    async deleteDanger(danger) {
      await dangersRef.doc(danger.id).delete();
      await Promise.allSettled((danger.images || []).map((image) => firebase.storage().ref(image.path).delete()));
    },
    // "Signaler aux administrateurs": a signed-in user flags a report with a comment.
    async flagDanger(danger, comment, user) {
      await dangerFlagsRef.add({
        danger_id: danger.id,
        section_id: danger.section_id,
        comment: (comment || "").trim(),
        by: userRef(user),
        created_at: serverTimestamp(),
        resolved: false,
        resolved_by: null,
        resolved_at: null,
      });
    },
    async getDangerFlags() {
      const snapshot = await dangerFlagsRef.orderBy("created_at", "desc").get();
      return snapshot.docs.map(toDanger);
    },
    async setFlagResolved(id, resolved, user) {
      await dangerFlagsRef.doc(id).update({
        resolved: Boolean(resolved),
        resolved_by: resolved ? userRef(user) : null,
        resolved_at: resolved ? serverTimestamp() : null,
      });
    },
  },
};
